import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "../../components/PageHero.jsx";
import { fetchPublicSeasons, fetchPublicStandings } from "../../lib/publicDbApi";
import { driverStatsPath } from "../../lib/driverStatsLinks";

const FIXED_RACE_COLUMNS = Array.from({ length: 12 }, (_, i) => `R${i + 1}`);
const DROP_COLUMNS = ["Drop 1", "Drop 2", "Drop 3"];

function toInt(value) {
    const n = parseInt(String(value ?? "").trim(), 10);
    return Number.isFinite(n) ? n : null;
}

function toNum(value) {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    if (!text) return null;

    const n = Number(text);
    return Number.isFinite(n) ? n : null;
}

function normalizeRaceKey(raw) {
    const s = String(raw ?? "").trim().toUpperCase();
    const m = s.match(/^R(\d{1,2})$/);
    if (!m) return null;
    const n = Number(m[1]);
    if (!Number.isFinite(n) || n < 1 || n > 12) return null;
    return `R${n}`;
}

function parseNumericPoints(cell) {
    const fromRaw = toNum(cell?.raw_points_value);
    if (fromRaw !== null) return fromRaw;
    const fromValue = toNum(cell?.points_value);
    if (fromValue !== null) return fromValue;
    const label = String(cell?.label ?? "").trim();
    if (/^-?\d+(\.\d+)?$/.test(label)) return Number(label);
    return null;
}

function formatNumericLabel(value) {
    const n = toNum(value);
    if (n === null) return null;
    return Number.isInteger(n) ? String(n) : String(n);
}

function isStatusLikeLabel(label) {
    const text = String(label ?? "").trim().toUpperCase();
    return text.includes("DNS") || text.includes("DNF") || text.includes("DSQ");
}

function getOriginalPointsLabel(cell) {
    const fromRaw = formatNumericLabel(cell?.raw_points_value);
    if (fromRaw !== null) return fromRaw;

    const fromPointsRaw = formatNumericLabel(cell?.points_raw_value);
    if (fromPointsRaw !== null) return fromPointsRaw;

    const fromOriginal = formatNumericLabel(cell?.original_points_value);
    if (fromOriginal !== null) return fromOriginal;

    const fromBeforeDrop = formatNumericLabel(cell?.points_before_drop);
    if (fromBeforeDrop !== null) return fromBeforeDrop;

    const fromValue = formatNumericLabel(cell?.points_value);
    if (fromValue !== null) return fromValue;

    return null;
}

function displayRaceCellLabel(cell) {
    if (!cell) return "-";

    const label = String(cell?.label ?? "").trim();

    if (isStatusLikeLabel(label)) {
        return label;
    }

    const originalPoints = getOriginalPointsLabel(cell);
    if (originalPoints !== null) {
        return originalPoints;
    }

    return label || "-";
}

function displayDropCellLabel(cell) {
    if (!cell) return "0";

    const originalPoints = getOriginalPointsLabel(cell);
    if (originalPoints !== null) {
        return originalPoints;
    }

    const label = String(cell?.label ?? "").trim();
    if (!label || label === "-") return "0";

    return label;
}

function statusClass(label) {
    const text = String(label ?? "").trim().toUpperCase();
    if (!text) return "";
    if (text.includes("DNS")) return "stg-score--dns";
    if (text.includes("DNF")) return "stg-score--dnf";
    if (text.includes("DSQ")) return "stg-score--dsq";
    return "";
}

function deltaMeta(delta) {
    const n = toInt(delta) ?? 0;
    if (n > 0) return { text: `▲ ${n}`, cls: "stg-delta-up" };
    if (n < 0) return { text: `▼ ${Math.abs(n)}`, cls: "stg-delta-down" };
    return { text: "-", cls: "stg-delta-same" };
}

function initials(name) {
    const s = String(name || "").trim();
    if (!s) return "?";
    return s
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
}

function classPillClass(classKey, scopeType) {
    if (scopeType === "TEAM") return "pill--team";
    if (scopeType === "BRAND") return "pill--brand";
    const key = String(classKey || "").toUpperCase();
    if (key === "PRO") return "pill--pro";
    if (key === "INT") return "pill--int";
    if (key === "AM") return "pill--am";
    return "pill--gesamt";
}

function getRaceCellForColumn(scores, raceCol) {
    const direct = scores?.[raceCol];
    if (direct) return direct;
    const byAny = Object.entries(scores || {}).find(([k, v]) => {
        if (normalizeRaceKey(k) === raceCol) return true;
        return normalizeRaceKey(v?.race_tag) === raceCol;
    });
    return byAny?.[1] || null;
}

function computeDropEntries(scores, raceColumns = FIXED_RACE_COLUMNS) {
    const explicit = [];
    const candidates = [];

    for (const raceCol of raceColumns) {
        const normalized = normalizeRaceKey(raceCol);
        const raceNo = normalized ? Number(normalized.slice(1)) : 999;
        const cell = getRaceCellForColumn(scores, raceCol);

        const labelRaw = String(cell?.label ?? "").trim();
        const isDropped = Boolean(cell?.is_dropped);
        const pts = parseNumericPoints(cell);
        const hasData = Boolean(cell && ((labelRaw && labelRaw !== "-") || pts !== null));

        const entry = {
            raceNo,
            race: normalized || String(raceCol || "-"),
            label: displayDropCellLabel(cell),
            points: pts ?? 0,
            hasData,
            isDropped,
        };

        if (isDropped) explicit.push(entry);
        candidates.push(entry);
    }

    explicit.sort((a, b) => a.raceNo - b.raceNo);
    if (explicit.length >= 3) return explicit.slice(0, 3);

    const used = new Set(explicit.map((e) => e.race));
    const fallback = candidates
        .filter((e) => !used.has(e.race))
        .sort((a, b) => {
            if (a.points !== b.points) return a.points - b.points;
            if (a.hasData !== b.hasData) return a.hasData ? -1 : 1;
            return b.raceNo - a.raceNo;
        });

    const out = [...explicit];
    for (const item of fallback) {
        if (out.length >= 3) break;
        out.push(item);
    }

    while (out.length < 3) {
        const raceNo = 13 - out.length;
        out.push({
            raceNo,
            race: `R${raceNo}`,
            label: "0",
            points: 0,
            hasData: false,
            isDropped: false,
        });
    }

    return out.slice(0, 3);
}

function formatPoints(value) {
    const n = toNum(value);
    if (n === null) return "0";
    return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
}

function TeamBreakdownCard({ row }) {
    const breakdown = Array.isArray(row?.driver_breakdown) ? row.driver_breakdown : [];

    return (
        <div
            style={{
                margin: "10px 0 14px",
                padding: "14px 16px",
                borderRadius: 18,
                border: "1px solid rgba(255,255,255,.10)",
                background:
                    "linear-gradient(135deg, rgba(228,254,60,.075), rgba(255,255,255,.035))",
                boxShadow: "0 18px 44px rgba(0,0,0,.20)",
            }}
        >
            <div
                style={{
                    textAlign: "center",
                    marginBottom: 12,
                }}
            >
                <strong style={{ fontSize: 15 }}>
                    Teamfahrer
                </strong>
            </div>

            {breakdown.length ? (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 10,
                    }}
                >
                    {breakdown.map((driver) => {
                        const counted = Number(driver?.counted_points || 0);
                        const notCounted = Number(driver?.not_counted_points || 0);

                        return (
                            <div
                                key={driver.driver_name}
                                style={{
                                    borderRadius: 14,
                                    padding: "12px 14px",
                                    background: "rgba(0,0,0,.20)",
                                    border: "1px solid rgba(255,255,255,.08)",
                                    display: "grid",
                                    gap: 7,
                                    textAlign: "center",
                                    justifyItems: "center",
                                }}
                            >
                                <div style={{ fontWeight: 800 }}>
                                    {driver.driver_name || "-"}
                                </div>

                                <div
                                    style={{
                                        color: "var(--accent, #e4fe3c)",
                                        fontWeight: 900,
                                    }}
                                >
                                    {formatPoints(counted)} Punkte
                                </div>

                                <div className="muted" style={{ fontSize: 12 }}>
                                    ({formatPoints(notCounted)} nicht gezählt)
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="muted" style={{ textAlign: "center" }}>
                    Für dieses Team wurden noch keine Fahrer-Beiträge gefunden.
                </div>
            )}
        </div>
    );
}

function TopCard({ row, rank, scopeType, seasonKey }) {
    const points = toInt(row?.counted_points) ?? 0;
    const diffLead = toInt(row?.diff_to_leader) ?? 0;
    const carNo = String(row?.car_number_snapshot || "").trim();
    const displayName = String(row?.display_name_snapshot || "-");
    const teamName = String(row?.team_name_snapshot || "").trim();

    const driverImage = String(row?.driver_image_url_resolved || "").trim();
    const teamLogo = String(row?.team_logo_url_snapshot || "").trim();
    const brandLogo = String(row?.brand_logo_url_snapshot || "").trim();
    const carLogo = String(row?.car_image_url_snapshot || "").trim();

    let mainImage = driverImage;
    let title = displayName;
    let subtitle = carNo ? `#${carNo}` : "";
    let logoLeft = brandLogo || carLogo;
    let logoRight = teamLogo;

    if (scopeType === "TEAM") {
        mainImage = teamLogo;
        title = teamName || displayName;
        subtitle = "Team";
        logoLeft = "";
        logoRight = "";
    } else if (scopeType === "BRAND") {
        mainImage = brandLogo || carLogo;
        title = displayName;
        subtitle = "Marke";
        logoLeft = "";
        logoRight = "";
    }

    return (
        <article className={`stg-top-card stg-top-card--rank${rank}`}>
            <div className="stg-top-card__head">
                <div className="stg-top-rank">{rank}</div>
                {rank === 1 && <div className="stg-top-badge">Fuehrender</div>}
            </div>

            <div className="stg-top-body">
                <div className="stg-top-avatar-wrap">
                    {mainImage ? (
                        <img src={mainImage} alt={title} className="stg-top-avatar" loading="lazy" />
                    ) : (
                        <div className="stg-top-avatar-fallback">{initials(title)}</div>
                    )}
                </div>

                <div className="stg-top-meta">
                    <h3>
                        {scopeType === "DRIVER" && row?.driver_id ? (
                            <Link
                                to={driverStatsPath(row, seasonKey)}
                                className="rv-driver-link"
                            >
                                {title}
                            </Link>
                        ) : (
                            title
                        )}
                    </h3>
                    {subtitle && <div className="stg-top-sub">{subtitle}</div>}
                    {(logoLeft || logoRight) && (
                        <div className="stg-top-logos">
                            {logoLeft ? <img src={logoLeft} alt="Logo" loading="lazy" /> : null}
                            {logoRight ? <img src={logoRight} alt="Logo" loading="lazy" /> : null}
                        </div>
                    )}
                </div>
            </div>

            <div className="stg-top-footer">
                <strong>{points} Punkte</strong>
                <span>{diffLead > 0 ? `+${diffLead}` : "+0"} Differenz</span>
            </div>
        </article>
    );
}

export default function StandingsBase({
    seasonKey = "active",
    scopeType = "DRIVER",
    classKey = "",
    heading = "Meisterschaftswertung",
    pillLabel = "",
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [resolvedSeasonKey, setResolvedSeasonKey] = useState(seasonKey);
    const [openTeamDetails, setOpenTeamDetails] = useState({});

    function toggleTeamDetails(rowId) {
        const key = String(rowId || "");
        setOpenTeamDetails((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    }

    useEffect(() => {
        (async () => {
            setLoading(true);
            setError("");
            try {
                let nextSeasonKey = String(seasonKey || "active");
                const res = await fetchPublicStandings({
                    seriesKey: "gt3x",
                    seasonKey: nextSeasonKey,
                    scopeType,
                    classKey,
                }).catch(async (err) => {
                    const msg = String(err?.message || err || "");
                    const shouldFallback = msg.includes("Season not found") || msg.includes("HTTP 404");
                    if (!shouldFallback) throw err;

                    const seasonsData = await fetchPublicSeasons("gt3x");
                    const seasons = Array.isArray(seasonsData?.seasons) ? seasonsData.seasons : [];
                    const activeSeason = seasons.find((s) => String(s?.status || "").toLowerCase() === "active");
                    const fallbackSeason = activeSeason?.season_key || seasons[0]?.season_key;
                    if (!fallbackSeason) throw err;
                    nextSeasonKey = String(fallbackSeason);
                    return fetchPublicStandings({
                        seriesKey: "gt3x",
                        seasonKey: nextSeasonKey,
                        scopeType,
                        classKey,
                    });
                });

                setResolvedSeasonKey(nextSeasonKey);
                setData(res);
            } catch (err) {
                setError(err?.message || String(err));
                setData(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [scopeType, classKey, seasonKey]);

    const rows = useMemo(() => {
        const list = Array.isArray(data?.rows) ? [...data.rows] : [];
        list.sort((a, b) => (toInt(a?.position) ?? 999) - (toInt(b?.position) ?? 999));
        return list;
    }, [data]);

    const topThree = useMemo(() => rows.slice(0, 3), [rows]);

    const racePointLeaders = useMemo(() => {
        const map = new Map();
        for (const raceCol of FIXED_RACE_COLUMNS) {
            const entries = [];
            for (const row of rows) {
                const scores = row?.scores || {};
                const cell = getRaceCellForColumn(scores, raceCol);
                const pts = parseNumericPoints(cell);
                if (pts === null) continue;
                entries.push({ rowId: row.id, points: pts });
            }
            entries.sort((a, b) => b.points - a.points);
            map.set(raceCol, entries.slice(0, 3));
        }
        return map;
    }, [rows]);

    const tableRows = useMemo(() => {
        return rows.map((row, idx) => {
            const prev = idx > 0 ? rows[idx - 1] : null;
            const prevPts = toInt(prev?.counted_points);
            const curPts = toInt(row?.counted_points) ?? 0;
            const gapPrev = idx === 0 || prevPts === null ? 0 : Math.max(prevPts - curPts, 0);

            const scores = row?.scores || {};
            const raceCells = FIXED_RACE_COLUMNS.map((raceCol) => getRaceCellForColumn(scores, raceCol));
            const dropped = computeDropEntries(scores).map((item) => ({
                label: String(item.label || "0"),
                race: item.race || "-",
            }));

            return {
                row,
                gapPrev,
                raceCells,
                dropped,
            };
        });
    }, [rows]);

    const pillText = pillLabel || (scopeType === "DRIVER" ? (classKey || "GESAMT") : scopeType);

    return (
        <div className="rv-page rv-results-testing stg-page">
            <PageHero kicker="GT3 Xperience" title={heading}>
                <span className={`pill ${classPillClass(classKey, scopeType)}`}>{pillText}</span>
                <span style={{ marginLeft: 8 }}>Saison {resolvedSeasonKey || "-"}</span>
            </PageHero>

            {!!error && (
                <div className="rv-modern-card">
                    <p className="rv-message rv-message--error" style={{ margin: 0 }}>
                        Fehler: {error}
                    </p>
                </div>
            )}

            {!loading && !error && topThree.length > 0 && (
                <section className="stg-top-grid">
                    {topThree[1] && <TopCard row={topThree[1]} rank={2} scopeType={scopeType} seasonKey={resolvedSeasonKey} />}
                    {topThree[0] && <TopCard row={topThree[0]} rank={1} scopeType={scopeType} seasonKey={resolvedSeasonKey} />}
                    {topThree[2] && <TopCard row={topThree[2]} rank={3} scopeType={scopeType} seasonKey={resolvedSeasonKey} />}
                </section>
            )}

            <section className="rt-table-wrap">
                <table className="rt-table stg-table">
                    <thead>
                        {scopeType === "DRIVER" ? (
                            <tr>
                                <th>Pos</th>
                                <th>+/-</th>
                                <th></th>
                                <th>Fahrername</th>
                                <th>#</th>
                                <th></th>
                                <th></th>
                                <th>Teamname</th>
                                <th>Punkte</th>
                                <th>Differenz</th>
                                {FIXED_RACE_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                                {DROP_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                            </tr>
                        ) : scopeType === "TEAM" ? (
                            <tr>
                                <th>Pos</th>
                                <th>+/-</th>
                                <th></th>
                                <th>Teamname</th>
                                <th>Punkte</th>
                                <th>Differenz</th>
                                {FIXED_RACE_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                                {DROP_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                            </tr>
                        ) : (
                            <tr>
                                <th>Pos</th>
                                <th>+/-</th>
                                <th></th>
                                <th>Autoname</th>
                                <th>Punkte</th>
                                <th>Differenz</th>
                                {FIXED_RACE_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                                {DROP_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                            </tr>
                        )}
                    </thead>

                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={scopeType === "DRIVER" ? 25 : 21} className="rt-empty">Lade...</td>
                            </tr>
                        )}

                        {!loading && !error && !tableRows.length && (
                            <tr>
                                <td colSpan={scopeType === "DRIVER" ? 25 : 21} className="rt-empty">Keine Daten.</td>
                            </tr>
                        )}

                        {!loading && !error && tableRows.map(({ row, gapPrev, raceCells, dropped }) => {
                            const delta = deltaMeta(row?.position_delta);
                            const points = toInt(row?.counted_points) ?? 0;
                            const diffText = row?.position === 1 ? "+0" : `+${gapPrev}`;
                            const isTeamOpen = scopeType === "TEAM" && !!openTeamDetails[String(row.id)];

                            return (
                                <Fragment key={row.id}>
                                    <tr>
                                        <td className="rt-num">{row?.position ?? "-"}</td>
                                        <td className={`rt-num ${delta.cls}`}>{delta.text}</td>

                                        {scopeType === "DRIVER" ? (
                                            <>
                                                <td className="rt-icon">
                                                    {row?.flag_url_snapshot ? (
                                                        <img
                                                            src={row.flag_url_snapshot}
                                                            alt="Flag"
                                                            className="rt-flag"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span className="rt-dash">-</span>
                                                    )}
                                                </td>

                                                <td className="rt-driver">
                                                    {row?.driver_id ? (
                                                        <Link
                                                            to={driverStatsPath(row, resolvedSeasonKey)}
                                                            className="rv-driver-link"
                                                        >
                                                            {row?.display_name_snapshot || "-"}
                                                        </Link>
                                                    ) : (
                                                        row?.display_name_snapshot || "-"
                                                    )}
                                                </td>

                                                <td className="rt-num">{row?.car_number_snapshot || "-"}</td>

                                                <td className="rt-icon">
                                                    {row?.brand_logo_url_snapshot ? (
                                                        <img
                                                            src={row.brand_logo_url_snapshot}
                                                            alt="Auto"
                                                            className="rt-brand-logo"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span className="rt-dash">-</span>
                                                    )}
                                                </td>

                                                <td className="rt-icon">
                                                    {row?.team_logo_url_snapshot ? (
                                                        <img
                                                            src={row.team_logo_url_snapshot}
                                                            alt="Team"
                                                            className="rt-team-logo"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span className="rt-dash">-</span>
                                                    )}
                                                </td>

                                                <td>{row?.team_name_snapshot || "-"}</td>
                                            </>
                                        ) : scopeType === "TEAM" ? (
                                            <>
                                                <td className="rt-icon">
                                                    {row?.team_logo_url_snapshot ? (
                                                        <img
                                                            src={row.team_logo_url_snapshot}
                                                            alt="Team"
                                                            className="rt-team-logo"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span className="rt-dash">-</span>
                                                    )}
                                                </td>

                                                <td className="rt-driver">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleTeamDetails(row.id)}
                                                        aria-label={
                                                            openTeamDetails[String(row.id)]
                                                                ? "Team einklappen"
                                                                : "Team ausklappen"
                                                        }
                                                        style={{
                                                            marginRight: 8,
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: 999,
                                                            border: "1px solid rgba(255,255,255,.16)",
                                                            background: openTeamDetails[String(row.id)]
                                                                ? "rgba(228,254,60,.18)"
                                                                : "rgba(255,255,255,.07)",
                                                            color: "var(--text)",
                                                            cursor: "pointer",
                                                            fontWeight: 900,
                                                            lineHeight: "20px",
                                                        }}
                                                    >
                                                        {openTeamDetails[String(row.id)] ? "−" : "+"}
                                                    </button>

                                                    {row?.display_name_snapshot || row?.team_name_snapshot || "-"}
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="rt-icon">
                                                    {row?.brand_logo_url_snapshot ? (
                                                        <img
                                                            src={row.brand_logo_url_snapshot}
                                                            alt="Brand"
                                                            className="rt-brand-logo"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span className="rt-dash">-</span>
                                                    )}
                                                </td>

                                                <td className="rt-driver">
                                                    {row?.display_name_snapshot || row?.brand_name_snapshot || "-"}
                                                </td>
                                            </>
                                        )}

                                        <td className="stg-points">{points}</td>
                                        <td className="rt-time">{diffText}</td>

                                        {raceCells.map((cell, idx) => {
                                            const raceCol = FIXED_RACE_COLUMNS[idx];
                                            const txt = displayRaceCellLabel(cell);
                                            const clsStatus = statusClass(txt);

                                            let rankCls = "";
                                            if (!clsStatus && txt !== "-") {
                                                const leaders = racePointLeaders.get(raceCol) || [];
                                                const pos = leaders.findIndex((x) => x.rowId === row.id);

                                                if (pos === 0) rankCls = "stg-score--gold";
                                                if (pos === 1) rankCls = "stg-score--silver";
                                                if (pos === 2) rankCls = "stg-score--bronze";
                                            }

                                            return (
                                                <td key={`${row.id}-${raceCol}`}>
                            <span className={`stg-score-pill ${clsStatus} ${rankCls}`.trim()}>
                                {txt}
                            </span>
                                                </td>
                                            );
                                        })}

                                        {dropped.map((drop, i) => (
                                            <td key={`${row.id}-drop-${i}`}>
                                                <div className="stg-drop-cell">
                                                    <div>{drop.label}</div>
                                                    <small>({drop.race || "-"})</small>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {isTeamOpen && (
                                        <tr>
                                            <td colSpan={21} style={{ padding: "0 14px 12px" }}>
                                                <TeamBreakdownCard row={row} />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
